import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTheme } from '../../context/ThemeContext';
import { getTheme } from '../../theme';
import MainLayout from '../Layout/MainLayout';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from './Table';
import Button from './Button';
import Card from './Card';
import { Add, Edit, Delete, Search } from '@mui/icons-material';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.lg};
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  margin: 0;
  ${props => props.theme?.isDark && `
    text-shadow: 0 0 10px rgba(0, 255, 0, 0.3);
  `}
`;

const Actions = styled.div`
  display: flex;
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
  align-items: center;
`;

const SearchInput = styled.input`
  padding: ${props => getTheme(props.theme?.isDark).spacing.sm} ${props => getTheme(props.theme?.isDark).spacing.md};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${props => getTheme(props.theme?.isDark).colors.surface};
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
  min-width: 250px;

  &:focus {
    outline: none;
    border-color: ${props => getTheme(props.theme?.isDark).colors.primary};
    ${props => props.theme?.isDark && `
      box-shadow: 0 0 10px rgba(0, 255, 0, 0.3);
    `}
  }
`;

const TableCard = styled(Card)`
  overflow-x: auto;
`;

const ActionButton = styled.button`
  padding: ${props => getTheme(props.theme?.isDark).spacing.xs};
  background: transparent;
  border: none;
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  cursor: pointer;
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.sm};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;

  &:hover {
    background-color: ${props => getTheme(props.theme?.isDark).colors.hover};
    color: ${props => getTheme(props.theme?.isDark).colors.primary};
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${props => getTheme(props.theme?.isDark).spacing.md};
`;

const PaginationInfo = styled.div`
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  font-size: 14px;
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: ${props => getTheme(props.theme?.isDark).spacing.sm};
`;

const ResourceList = ({
  title,
  resource,
  apiMethod,
  columns,
  onEdit,
  onDelete,
  onCreate,
  searchFields = [],
}) => {
  const { isDark } = useTheme();
  const theme = { isDark };
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, [page, search]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const filter = {};
      if (search && searchFields.length > 0) {
        // Simple search - can be enhanced
        filter.search = search;
      }
      const response = await apiMethod({
        page,
        limit,
        filter,
      });
      setData(response.results || response.docs || response.data || []);
      setTotal(response.totalResults || response.total || 0);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.response?.data?.message || error.message || 'Xəta baş verdi');
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await onDelete(id);
        fetchData();
      } catch (error) {
        console.error('Error deleting:', error);
      }
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <MainLayout>
      <PageContainer theme={theme}>
        <PageHeader>
          <Title theme={theme}>{title}</Title>
          <Actions>
            {searchFields.length > 0 && (
              <div style={{ position: 'relative' }}>
                <Search style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: getTheme(isDark).colors.textSecondary }} />
                <SearchInput
                  type="text"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  style={{ paddingLeft: '36px' }}
                  theme={theme}
                />
              </div>
            )}
            {onCreate && (
              <Button onClick={() => navigate(`/admin/${resource}/new`)} theme={theme}>
                <Add /> Create New
              </Button>
            )}
          </Actions>
        </PageHeader>
        <TableCard theme={theme}>
          <Table theme={theme}>
            <TableHead theme={theme}>
              <TableRow theme={theme}>
                {columns.map((col) => (
                  <TableHeader key={col.key} theme={theme}>
                    {col.label}
                  </TableHeader>
                ))}
                <TableHeader theme={theme}>Actions</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow theme={theme}>
                  <TableCell colSpan={columns.length + 1} theme={theme}>
                    Yüklənir...
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow theme={theme}>
                  <TableCell colSpan={columns.length + 1} theme={theme} style={{ color: '#ef4444' }}>
                    Xəta: {error}
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow theme={theme}>
                  <TableCell colSpan={columns.length + 1} theme={theme}>
                    Məlumat tapılmadı
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row) => (
                  <TableRow key={row.id} theme={theme}>
                    {columns.map((col) => (
                      <TableCell key={col.key} theme={theme}>
                        {col.render ? col.render(row[col.key], row) : row[col.key]}
                      </TableCell>
                    ))}
                    <TableCell theme={theme}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {onEdit && (
                          <ActionButton
                            onClick={() => onEdit(row.id)}
                            theme={theme}
                            title={resource === 'test-runs' ? 'View Details' : 'Edit'}
                          >
                            <Edit fontSize="small" />
                          </ActionButton>
                        )}
                        {onDelete && (
                          <ActionButton
                            onClick={() => handleDelete(row.id)}
                            theme={theme}
                            title="Delete"
                          >
                            <Delete fontSize="small" />
                          </ActionButton>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {totalPages > 1 && (
            <Pagination theme={theme}>
              <PaginationInfo theme={theme}>
                Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total}
              </PaginationInfo>
              <PaginationButtons>
                <Button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  theme={theme}
                >
                  Previous
                </Button>
                <Button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  theme={theme}
                >
                  Next
                </Button>
              </PaginationButtons>
            </Pagination>
          )}
        </TableCard>
      </PageContainer>
    </MainLayout>
  );
};

export default ResourceList;

